"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppFirstParty } from "@/hooks/use-app-first-party";
import type { FirstPartyCatalog } from "@/lib/firstparty/types";
import { countWalkthroughs, walkthroughLabel } from "@/lib/firstparty/normalize";
import { readScreensParams, screensSearch } from "@/lib/firstparty/designs-url";
import { categoryCover, categoryLabel, designCategory, flowLabel, groupByCategory, screenCategory, type DesignCategory } from "@/lib/firstparty/design-categories";
import { cycleScreens } from "@/lib/firstparty/hover-cycle";
import { ScreenLightbox, type LightboxItem } from "@/components/appstore/screen-lightbox";
import { GalleryFilter, MotionGrid, ScreenTile, TILE_GRID, TILE_SIZES, AnimationTile, WalkthroughButton, flowClips, flowMeta, fmtDur } from "@/components/gallery/screen-tile";
import { SECTION_TITLE } from "@/components/ui/typography";
import { GATE_BODY, GATE_TITLE, SignInGate, useSignInGate } from "@/components/designs/sign-in-gate";

interface FirstPartyScreensProps {
  appName: string;
  catalog: FirstPartyCatalog;
  className?: string;
}

type View = "all" | DesignCategory | "motion";
type Flow = FirstPartyCatalog["flows"][number];

/** Every screen of the flows as viewer items, each captioned with its flow. */
function screenItems(appName: string, flows: Flow[]): LightboxItem[] {
  return flows.flatMap((flow) =>
    flow.screens.map((screen, i) => ({
      src: screen.image_url,
      alt: screen.screen_name || `${appName} ${flow.name} step ${i + 1}`,
      caption: `${flowLabel(flow.name, flow.category)} · ${screen.screen_name || `Step ${i + 1}`}`,
    })),
  );
}

/**
 * Our own captured screens in the four design categories (onboarding, paywall & subscription,
 * features, other) plus motion. "All" shows one screen per category; a category shows its flows in
 * recording order, each with where it starts; a flow shows its screens and then its own motion
 * clips. Any screen or clip opens full size. Shown only when the app has a published catalog.
 */
export const FirstPartyScreens = memo(function FirstPartyScreens({ appName, catalog, className }: FirstPartyScreensProps) {
  const flows = catalog.flows ?? [];
  const animations = catalog.animations ?? [];
  const [chosenView, setView] = useState<View>("all");
  const [chosenFlow, setOpenFlow] = useState<Flow | null>(null);
  const gate = useSignInGate("Sign in free to see every app's onboarding, paywall and feature screens.", "app-page-designs-gate");
  const gated = gate.state === "gated";
  const view = gated ? "all" : chosenView;
  const openFlow = gated ? null : chosenFlow;
  const [lightbox, setLightbox] = useState<{ items: LightboxItem[]; index: number } | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const flowPushed = useRef(false);

  // The open category and flow live in the address (?cat=&flow=), each an entry of its own, so Back
  // steps out one level and a shared link opens the same place.
  useEffect(() => {
    const read = () => {
      const shown = [...groupByCategory(catalog.flows ?? []).map((g) => g.slug), ...((catalog.animations?.length ?? 0) > 0 ? ["motion"] : [])];
      const { cat, flow } = readScreensParams(window.location.search, shown);
      setView((cat as View | null) ?? "all");
      setOpenFlow(flow ? (catalog.flows ?? []).find((f) => f.name === flow && designCategory(f.category) === cat) ?? null : null);
      flowPushed.current = false;
      return Boolean(cat);
    };
    if (read()) sectionRef.current?.scrollIntoView({ block: "start" });
    const onPop = () => void read();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [catalog]);
  // A view already in the address adds no entry; leaving a flow opened by a link replaces it.
  const go = (next: View, flow: Flow | null, replace = false) => {
    // Clicks are held by the section; this catches the phone's category dropdown.
    if (gate.state !== "open") return void (gated && gate.openLogin());
    const search = screensSearch(window.location.search, next === "all" ? null : next, flow?.name ?? null);
    if (search !== window.location.search) {
      const url = `${window.location.pathname}${search}`;
      if (replace) window.history.replaceState(null, "", url);
      else window.history.pushState(null, "", url);
      flowPushed.current = !replace && Boolean(flow);
    }
    setView(next);
    setOpenFlow(flow);
  };
  const closeFlow = () => (flowPushed.current ? window.history.back() : go(view, null, true));

  const totalScreens = flows.reduce((n, f) => n + f.screens.length, 0);
  const totalVideos = countWalkthroughs(catalog);
  const poster = flows[0]?.screens[0]?.image_url;
  const groups = useMemo(
    () =>
      groupByCategory(flows).map((g) => {
        const screens = g.flows.flatMap((f) => f.screens.map((screen) => ({ ...screen, category: screenCategory(screen, f.category) })));
        const cover = categoryCover(g.slug, screens);
        return { ...g, count: screens.length, cover, cycle: cycleScreens(screens.map((x) => x.image_url), cover?.image_url) };
      }),
    [flows],
  );

  const walkthroughItems: LightboxItem[] = useMemo(() => {
    const oneX = (catalog.walkthroughs ?? []).filter((w) => w.speed === "1x" || !w.speed);
    return [
      ...(catalog.full_video
        ? [{ src: catalog.full_video, alt: `${appName} full walkthrough`, caption: "Full walkthrough · personal details removed", type: "video" as const, poster }]
        : []),
      ...oneX.map((w) => ({
        src: w.url,
        alt: `${appName} ${walkthroughLabel(w.part).toLowerCase()}`,
        caption: [walkthroughLabel(w.part), w.duration_s ? fmtDur(w.duration_s) : null].filter(Boolean).join(" · "),
        type: "video" as const,
        poster,
      })),
    ];
  }, [appName, catalog.full_video, catalog.walkthroughs, poster]);
  // The button opens the first walkthrough, so it shows that one's length.
  const walkthroughDuration = catalog.full_video
    ? catalog.full_video_duration_s
    : (catalog.walkthroughs ?? []).find((w) => w.speed === "1x" || !w.speed)?.duration_s;

  if (flows.length === 0) return null;

  const pick = (next: View) => go(next, null);
  const flowItems = (flow: Flow): LightboxItem[] => [
    ...(flow.clip_url ? [{ src: flow.clip_url, alt: `${appName} ${flow.name} walkthrough`, caption: `${flowLabel(flow.name, flow.category)} · walkthrough`, type: "video" as const, poster: flow.screens[0]?.image_url }] : []),
    ...screenItems(appName, [flow]),
  ];
  const group = groups.find((g) => g.slug === view);

  return (
    <section ref={sectionRef} onClickCapture={gate.onClickCapture} className={cn("relative w-full scroll-mt-20", className)} aria-label={`Real in-app screens for ${appName}`}>
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
          <h2 className={cn("min-w-0", SECTION_TITLE)}>Inside the app</h2>
          <div className="flex items-center gap-2">
            {walkthroughItems.length > 0 ? (
              <WalkthroughButton duration={walkthroughDuration} onClick={() => setLightbox({ items: walkthroughItems, index: 0 })} />
            ) : null}
          </div>
        </div>
        <p className="mt-1.5 max-w-2xl text-[15px] leading-6 text-muted-foreground">
          {totalScreens} screens across {flows.length} flows
          {totalVideos > 0 && <> and {totalVideos} {totalVideos === 1 ? "walkthrough" : "walkthroughs"}</>}, captured and checked by App Store Tracker.
        </p>
      </div>

      <GalleryFilter
        label="Show screens by category"
        value={view}
        onChange={(value) => pick(value as View)}
        selectClassName="mb-8"
        chipsClassName="-mx-1 mb-8 items-center gap-1 overflow-x-auto px-1 scrollbar-hide sm:flex"
        options={[
          { value: "all", label: "All", count: totalScreens },
          ...groups.map((g) => ({ value: g.slug, label: g.label, count: g.count })),
          ...(animations.length > 0 ? [{ value: "motion", label: "Motion", count: animations.length }] : []),
        ]}
      />

      {openFlow ? (
        <div>
          <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <button type="button" onClick={closeFlow} className="-my-2 inline-flex h-10 items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> {categoryLabel(designCategory(openFlow.category))}
            </button>
            <h3 className="text-lg font-semibold tracking-[-0.015em] sm:text-xl">{flowLabel(openFlow.name, openFlow.category)}</h3>
            <span className="text-[13px] text-muted-foreground">{flowMeta(openFlow)}</span>
          </div>
          <div className={TILE_GRID}>
            {openFlow.clip_url && (
              <ScreenTile
                src={openFlow.screens[0]?.image_url}
                alt={`${openFlow.name} walkthrough`}
                sizes={TILE_SIZES}
                caption="Walkthrough"
                meta={openFlow.clip_dur_s ? fmtDur(openFlow.clip_dur_s) : null}
                video
                onOpen={() => setLightbox({ items: flowItems(openFlow), index: 0 })}
              />
            )}
            {openFlow.screens.map((screen, i) => (
              <ScreenTile
                key={screen.id}
                src={screen.image_url}
                alt={screen.screen_name || `${appName} screen ${i + 1}`}
                sizes={TILE_SIZES}
                caption={screen.screen_name || `Step ${i + 1}`}
                meta={`${i + 1}`}
                onOpen={() => setLightbox({ items: flowItems(openFlow), index: i + (openFlow.clip_url ? 1 : 0) })}
              />
            ))}
            {flowClips(animations, openFlow.name).map((animation, i) => (
              <AnimationTile key={`${animation.mp4_url}-${i}`} animation={animation} />
            ))}
          </div>
        </div>
      ) : (
        <>
          {view === "all" && (
            <div className={TILE_GRID}>
              {groups.map((g) => (
                <ScreenTile
                  key={g.slug}
                  src={g.cover?.image_url}
                  alt={`${g.label} in ${appName}`}
                  sizes={TILE_SIZES}
                  caption={g.label}
                  meta={`${g.count}`}
                  cycle={g.cycle}
                  onOpen={() => pick(g.slug)}
                />
              ))}
              {animations.length > 0 && (
                <ScreenTile
                  src={animations[0].poster_url}
                  alt={`Motion in ${appName}`}
                  sizes={TILE_SIZES}
                  caption="Motion"
                  meta={`${animations.length}`}
                  video
                  onOpen={() => pick("motion")}
                />
              )}
            </div>
          )}
          {gated && (
            <SignInGate
              className="mt-8"
              title={GATE_TITLE}
              body={GATE_BODY}
              onSignIn={gate.openLogin}
              newsletter={gate.newsletter}
            >
              <div inert className="max-h-[26rem] overflow-hidden">
                <FlowCovers flows={flows.slice(0, 10)} onOpen={() => undefined} />
              </div>
            </SignInGate>
          )}
          {group && <FlowCovers flows={group.flows} onOpen={(flow) => go(view, flow)} />}
          {view === "motion" && <MotionGrid animations={animations} />}
        </>
      )}

      <ScreenLightbox
        items={lightbox?.items ?? []}
        index={lightbox ? lightbox.index : null}
        onIndex={(index) => setLightbox((current) => (current ? { ...current, index } : current))}
        onClose={() => setLightbox(null)}
      />
    </section>
  );
});

/** One cover per flow, its first screen, with the flow's name, size and where it starts. */
function FlowCovers({ flows, onOpen }: { flows: Flow[]; onOpen: (flow: Flow) => void }) {
  const cycles = useMemo(() => flows.map((flow) => cycleScreens(flow.screens.map((s) => s.image_url))), [flows]);
  return (
    <div className={TILE_GRID}>
      {flows.map((flow, i) => (
        <ScreenTile
          key={`${flow.name}-${i}`}
          src={flow.screens[0]?.image_url}
          alt={`${flowLabel(flow.name, flow.category)} flow`}
          sizes={TILE_SIZES}
          caption={flowLabel(flow.name, flow.category)}
          meta={`${flow.screens.length}`}
          detail={flow.where}
          cycle={cycles[i]}
          onOpen={() => onOpen(flow)}
        />
      ))}
    </div>
  );
}

// App page mount: fetches the published catalog and renders nothing until it
// exists, so apps without our screens cost zero layout.
export function AppFirstPartyScreens({ appId, className }: { appId: number; className?: string }) {
  const { catalog } = useAppFirstParty(appId);
  if (!catalog || (catalog.flows ?? []).length === 0) return null;
  return <FirstPartyScreens appName={catalog.app} catalog={catalog} className={className} />;
}

export default FirstPartyScreens;
